/**
 * Ярлык состояния геометрии контура — один на весь продукт.
 *
 * Исходов четыре, и `unknown` — не пятая степень уверенности, а признанное
 * незнание: канонические выдачи кабинета про геометрию контура не говорят
 * ничего. Развилка `geometry !== 'none'` сводила бы незнание к утверждению
 * «полигон есть», то есть ровно к тому, ради чего значение и заводили.
 */
import type { GeometryStatus } from '../data/types';
import { defineMessages } from '../../i18n/define';
import { messageRecord } from '../lib/messageRecord';

const m = defineMessages({
  phraseAccepted: {
    id: 'data.geometry.phrase.accepted',
    defaultMessage: 'принятая привязка',
    description: 'Что известно про геометрию контура (фраза в сноске карточки контура): привязка полигона принята',
  },
  phraseCandidate: {
    id: 'data.geometry.phrase.candidate',
    defaultMessage: 'кандидатная привязка',
    description: 'Фраза про геометрию контура: полигон найден как кандидат, привязка не подтверждена',
  },
  phraseNone: {
    id: 'data.geometry.phrase.none',
    defaultMessage: 'нет привязки',
    description: 'Фраза про геометрию контура: привязки полигона нет',
  },
  phraseUnknown: {
    id: 'data.geometry.phrase.unknown',
    defaultMessage: 'источник о геометрии ничего не сказал',
    description: 'Фраза про геометрию контура: источник не ответил (это незнание, а не отсутствие полигона)',
  },
  cellShow: {
    id: 'data.geometry.cell.show',
    defaultMessage: 'показать',
    description: 'Подпись-действие в ячейке списка полей: показать контур на карте; коротко',
  },
  cellNone: {
    id: 'data.geometry.cell.none',
    defaultMessage: 'нет полигона',
    description: 'Ячейка списка полей: у контура нет полигона на карте; коротко',
  },
  cellUnknown: {
    id: 'data.geometry.cell.unknown',
    defaultMessage: 'нет данных',
    description: 'Ячейка списка полей: про геометрию контура ничего не известно; коротко',
  },
});

/** Что экран печатает про привязку контура. Словами, а не значком. */
export const GEOMETRY_PHRASE: Record<GeometryStatus, string> = messageRecord({
  accepted: m.phraseAccepted,
  candidate: m.phraseCandidate,
  none: m.phraseNone,
  unknown: m.phraseUnknown,
});

/**
 * Можно ли предложить «показать на карте». Только у названной привязки:
 * звать человека открыть полигон, которого источник не называл, — обещание,
 * которого экран не держит.
 */
export function hasPolygon(status: GeometryStatus): boolean {
  return status === 'accepted' || status === 'candidate';
}

/** Короткая подпись в ячейке списка: чего именно нет. */
/**
 * Геометрия контура с поправкой на выдачу карты (agroai-6al.27).
 *
 * Канон кабинета про геометрию не говорит (`unknown`), а карта на том же
 * экране полигоны уже получила: выдача `/map/contours` сужена до земли
 * хозяйства и несёт только контуры с геометрией. Значит, контур в выдаче —
 * «полигон есть», а отсутствие в ПОЛНОЙ выдаче — «полигона нет». Выдача
 * усечена или не прочитана (`onMap === null`) — незнание остаётся незнанием.
 *
 * Есть полигон — `candidate`, а не `accepted`: карта рисует лучший доступный
 * полигон, но принятость привязки этот ответ не утверждает.
 */
export function withMapEvidence(status: GeometryStatus, onMap: boolean | null): GeometryStatus {
  if (status !== 'unknown' || onMap === null) return status;
  return onMap ? 'candidate' : 'none';
}

export const GEOMETRY_CELL: Record<GeometryStatus, string> = messageRecord({
  accepted: m.cellShow,
  candidate: m.cellShow,
  none: m.cellNone,
  unknown: m.cellUnknown,
});
